class ShopTheLookV2 extends HTMLElement {
  constructor() {
    super();
    this.mode        = this.dataset.mode       || 'popup';
    this.layout      = this.dataset.layout     || 'grid';
    this.dotVisible  = this.dataset.dotVisible || 'always';
    this.activePopup = null;
    this.activeDot   = null;
    this.swiper      = null;
    this._backdrop   = null;
    this._mobilePopupOriginalParent = null;
  }

  connectedCallback() {
    this._initBackdrop();
    this._initDots();
    this._initCloseButtons();
    this._initATC();
    if (this.layout === 'carousel') {
      this._initCarousel();
    }
    if (this.dotVisible === 'hover') {
      this.classList.add('stlv2--dots-hover');
    }
    BlsLazyloadImg.init();
    this._bindOutsideClick();
    this._bindEscape();
  }

  /* ── Backdrop ── */
  _initBackdrop() {
    this._backdrop = document.createElement('div');
    this._backdrop.className = 'stlv2-popup-backdrop';
    this._backdrop.addEventListener('click', () => this._closeAllPopups());
    document.body.appendChild(this._backdrop);
  }

  _showBackdrop() {
    if (this._backdrop) this._backdrop.classList.add('is-open');
  }

  _hideBackdrop() {
    if (this._backdrop) this._backdrop.classList.remove('is-open');
  }

  /* ── Dots ── */
  _initDots() {
    this.querySelectorAll('.stlv2-dot').forEach(dot => {
      dot.addEventListener('click', e => {
        e.stopPropagation();
        if (this.mode === 'popup') {
          this._togglePopup(dot);
        } else {
          this._highlightStripItem(dot);
        }
      });
    });
  }

  /* ── Close buttons ── */
  _initCloseButtons() {
    this.querySelectorAll('.stlv2-popup-close').forEach(btn => {
      btn.addEventListener('click', e => {
        e.stopPropagation();
        this._closeAllPopups();
      });
    });
  }

  /* ── Popup ── */
  _togglePopup(dot) {
    const dotId    = dot.dataset.dotId;
    const imageWrap = dot.closest('.stlv2-image-wrap');
    const popup    = imageWrap
      ? imageWrap.querySelector(`.stlv2-popup[data-dot-id="${dotId}"]`)
      : null;
    const isActive = dot.classList.contains('active');
    this._closeAllPopups();
    if (!isActive && popup) {
      this._openPopup(dot, popup, imageWrap);
    }
  }

  _openPopup(dot, popup, imageWrap) {
    this._loadPopupImage(popup);

    const isMobile = window.innerWidth < 768;

    popup.classList.remove('popup-above', 'popup-below');
    popup.style.removeProperty('top');
    popup.style.removeProperty('bottom');
    popup.style.removeProperty('left');
    popup.style.removeProperty('right');
    popup.style.removeProperty('transform');

    if (isMobile) {
      /*
       * Move popup to <body> before adding is-open.
       * Any ancestor with CSS transform would scope position:fixed to that
       * ancestor instead of the viewport — moving to body bypasses that.
       */
      this._mobilePopupOriginalParent = popup.parentNode;
      document.body.appendChild(popup);
      popup.classList.add('is-open');
      this._showBackdrop();
    } else {
      /* Make popup briefly visible to measure its true dimensions */
      popup.style.visibility = 'hidden';
      popup.style.opacity    = '0';
      popup.style.display    = 'block';

      const dotRect  = dot.getBoundingClientRect();
      const wrapRect = imageWrap.getBoundingClientRect();
      const popupW   = popup.offsetWidth  || parseInt(getComputedStyle(this).getPropertyValue('--s2-popup-w')) || 290;
      const popupH   = popup.offsetHeight || 160;

      popup.style.removeProperty('display');
      popup.style.removeProperty('visibility');
      popup.style.removeProperty('opacity');

      /* Horizontal: center popup on dot, clamped within image-wrap */
      const dotCenterX = dotRect.left + dotRect.width / 2 - wrapRect.left;
      let left = dotCenterX - popupW / 2;
      left = Math.max(8, Math.min(left, wrapRect.width - popupW - 8));
      popup.style.left = left + 'px';

      /* Arrow horizontal offset: align arrow with dot center */
      const arrowLeft = Math.round(dotCenterX - left);
      popup.style.setProperty('--arrow-left', arrowLeft + 'px');

      /* Vertical: prefer below, fallback above */
      const dotBottomInWrap = dotRect.bottom - wrapRect.top;
      const dotTopInWrap    = dotRect.top    - wrapRect.top;
      const spaceBelow      = wrapRect.height - dotBottomInWrap;
      const spaceAbove      = dotTopInWrap;

      if (spaceBelow >= popupH + 16 || spaceBelow >= spaceAbove) {
        popup.style.top    = (dotBottomInWrap + 12) + 'px';
        popup.style.bottom = 'auto';
        popup.classList.add('popup-below');
      } else {
        popup.style.bottom = (wrapRect.height - dotTopInWrap + 12) + 'px';
        popup.style.top    = 'auto';
        popup.classList.add('popup-above');
      }

      popup.classList.add('is-open');
    }

    dot.classList.add('active');
    this.activePopup = popup;
    this.activeDot   = dot;
  }

  /* Popup product images are only fetched once a popup is actually opened,
     so they don't compete with the hero image for bandwidth on page load. */
  _loadPopupImage(popup) {
    const img = popup.querySelector('img[data-src]');
    if (!img) return;
    if (img.dataset.srcset) img.srcset = img.dataset.srcset;
    img.src = img.dataset.src;
    img.removeAttribute('data-src');
    img.removeAttribute('data-srcset');
  }

  _closeAllPopups() {
    /* If a mobile popup was moved to <body>, return it to its original parent */
    if (this.activePopup && this._mobilePopupOriginalParent) {
      this.activePopup.classList.remove('is-open', 'popup-above', 'popup-below');
      this._mobilePopupOriginalParent.appendChild(this.activePopup);
      this._mobilePopupOriginalParent = null;
    }

    /* Close any popups still inside the custom element */
    this.querySelectorAll('.stlv2-popup.is-open').forEach(p => {
      p.classList.remove('is-open', 'popup-above', 'popup-below');
    });

    /* Also close any body-level popup that may remain (safety net) */
    document.querySelectorAll('body > .stlv2-popup.is-open').forEach(p => {
      p.classList.remove('is-open', 'popup-above', 'popup-below');
      if (this._mobilePopupOriginalParent) {
        this._mobilePopupOriginalParent.appendChild(p);
        this._mobilePopupOriginalParent = null;
      }
    });

    this.querySelectorAll('.stlv2-dot.active').forEach(d => {
      d.classList.remove('active');
    });
    this._hideBackdrop();
    this.activePopup = null;
    this.activeDot   = null;
  }

  /* ── Above/below strip highlight ── */
  _highlightStripItem(dot) {
    const idx  = dot.dataset.dotIndex;
    const wrap = dot.closest('.stlv2-item');
    if (!wrap) return;

    wrap.querySelectorAll('.stlv2-dot').forEach(d => d.classList.remove('active'));
    wrap.querySelectorAll('.stlv2-strip-item').forEach(s => s.classList.remove('active'));

    dot.classList.add('active');
    const target = wrap.querySelector(`.stlv2-strip-item[data-product-index="${idx}"]`);
    if (target) {
      target.classList.add('active');
      target.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
    }
  }

  /* ── Outside click ── */
  _bindOutsideClick() {
    document.addEventListener('click', e => {
      if (!e.target.closest('.stlv2-dot') && !e.target.closest('.stlv2-popup')) {
        this._closeAllPopups();
      }
    });
  }

  /* ── Escape key ── */
  _bindEscape() {
    document.addEventListener('keydown', e => {
      if (e.key === 'Escape') this._closeAllPopups();
    });
  }

  /* ── Add to cart ── */
  _initATC() {
    this.querySelectorAll('.stlv2-atc-btn').forEach(btn => {
      btn.addEventListener('click', async e => {
        e.preventDefault();
        e.stopPropagation();
        if (btn.disabled) return;

        const variantId = btn.dataset.variantId;
        if (!variantId) return;

        btn.classList.add('is-loading');
        btn.disabled = true;

        try {
          const res = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
            body: JSON.stringify({ id: variantId, quantity: 1 })
          });

          if (res.ok) {
            btn.classList.remove('is-loading');
            btn.classList.add('is-added');
            const textEl = btn.querySelector('.stlv2-atc-text');
            if (textEl) {
              const original = textEl.textContent;
              textEl.textContent = '✓ Added';
              setTimeout(() => {
                textEl.textContent = original;
                btn.classList.remove('is-added');
                btn.disabled = false;
              }, 2000);
            }
            /* Update cart count in header if applicable */
            document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
          } else {
            throw new Error('Add to cart failed');
          }
        } catch {
          btn.classList.remove('is-loading');
          btn.disabled = false;
        }
      });
    });
  }

  /* ── Carousel (Swiper) ── */
  _initCarousel() {
    const el = this.querySelector('.stlv2-carousel');
    if (!el || el.swiper || el.classList.contains('swiper-initialized')) return;

    const desktop    = parseInt(el.dataset.desktop  || '2', 10);
    const tablet     = parseInt(el.dataset.tablet   || '1', 10);
    const mobile     = parseInt(el.dataset.mobile   || '1', 10);
    const spacing    = parseInt(el.dataset.spacing  || '20', 10);
    const autoplay   = el.dataset.autoplay === 'true';
    const loop       = el.dataset.loop    === 'true';
    const progressbar= el.dataset.paginationProgressbar === 'true';

    this.swiper = new Swiper('#' + el.id, {
      slidesPerView: mobile,
      spaceBetween: spacing,
      loop: loop,
      autoplay: autoplay ? { delay: 4000, disableOnInteraction: false } : false,
      speed: 500,
      watchSlidesProgress: true,
      navigation: {
        nextEl: this.querySelector('.swiper-button-next'),
        prevEl: this.querySelector('.swiper-button-prev'),
      },
      pagination: {
        clickable: true,
        el: this.querySelector('.swiper-pagination'),
        type: progressbar ? 'progressbar' : 'bullets',
      },
      breakpoints: {
        768:  { slidesPerView: tablet },
        1200: { slidesPerView: desktop },
      },
    });
  }
}

customElements.define('shop-the-look-v2', ShopTheLookV2);
